/**
 * Persistencia incremental de gravacao de audio em IndexedDB (M17.1) — resiliencia anti-perda.
 *
 * MOTIVACAO (incidente real 2026-06-21): o `useAudioRecorder` montava os chunks SO em memoria e
 * so os enviava no `stop`. Um reload da pagina (que o usuario deu para "recuperar" de um erro de
 * conexao apos um restart do server) destruia a gravacao em andamento -> perda total do audio.
 *
 * Solucao: cada `ondataavailable` (MediaRecorder.start(timeslice)) grava o chunk em IndexedDB sob
 * um `recordingId`. Se a pagina recarregar, a gravacao inacabada continua no disco do browser e
 * pode ser RECUPERADA (montada + transcrita ou baixada) no proximo mount.
 *
 * IndexedDB (e nao OPFS) como base: suporte universal + Blob nativo. OPFS seria a evolucao para
 * sessoes MUITO longas (ref Screenity), mas IndexedDB ja cobre o caso real com memoria baixa.
 *
 * DEGRADACAO GRACIOSA: sem IndexedDB (ou em erro), todas as operacoes viram no-op resolvido — o
 * hook segue funcionando em memoria como antes (sem resiliencia, mas sem quebrar).
 */

const DB_NAME = 'td-audio-recordings';
const DB_VERSION = 1;
const CHUNK_STORE = 'chunks';
const META_STORE = 'meta';

/**
 * FASE 133 (133.C.2) — idade maxima (ms) de uma gravacao CONCLUIDA (finished=true) antes do GC. 7 dias:
 * uma gravacao finalizada ha mais de 1 semana e ruido residual (os fluxos de sucesso ja fazem
 * `deleteRecording` na hora — o GC e a rede de seguranca para as que sobrarem finalizadas mas nao limpas).
 */
export const AUDIO_RECORDING_GC_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Metadados de uma gravacao persistida (1 por recordingId). */
export interface RecordingMeta {
  /** Id unico da gravacao (timestamp + random). Chave primaria do meta-store. */
  recordingId: string;
  /** Chave do consumidor: id da sessao do terminal ou 'chat' (qual contexto gravou). */
  key: string;
  /** mimeType do MediaRecorder (para remontar o Blob com o tipo certo). */
  mimeType: string;
  /** Epoch ms do inicio da gravacao. */
  startedAt: number;
  /** true quando o usuario parou normalmente (stop) — gravacoes finished nao sao "recuperaveis". */
  finished: boolean;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

/** Abre (1x) o IndexedDB. Resolve null se indisponivel — caller degrada para memoria. */
function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    try {
      if (typeof indexedDB === 'undefined' || !indexedDB) {
        resolve(null);
        return;
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(CHUNK_STORE)) {
          // chunks: key autoincrement; index por recordingId para varrer/montar/limpar.
          const store = db.createObjectStore(CHUNK_STORE, { keyPath: 'id', autoIncrement: true });
          store.createIndex('byRecording', 'recordingId', { unique: false });
        }
        if (!db.objectStoreNames.contains(META_STORE)) {
          db.createObjectStore(META_STORE, { keyPath: 'recordingId' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

/** Promisifica um IDBRequest. Rejeita em erro (callers tratam e degradam). */
function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Gera um id de gravacao unico e ordenavel (timestamp + sufixo aleatorio). */
export function newRecordingId(): string {
  return `rec_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Registra o inicio de uma gravacao (grava o meta com finished=false). No-op silencioso se o
 * IndexedDB estiver indisponivel.
 */
export async function beginRecording(meta: RecordingMeta): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const tx = db.transaction(META_STORE, 'readwrite');
    tx.objectStore(META_STORE).put(meta);
    await txDone(tx);
  } catch {
    /* degrada para memoria */
  }
}

/**
 * Persiste 1 chunk da gravacao. Chamado a cada `ondataavailable` (timeslice).
 *
 * Guarda o conteudo como ArrayBuffer (nao Blob): o structured-clone do IndexedDB preserva
 * ArrayBuffer de forma confiavel em TODOS os ambientes; Blobs tem pegadinhas (e o fake-indexeddb
 * em jsdom nem os clona). O Blob final e remontado em `assembleBlob`.
 */
export async function appendChunk(recordingId: string, blob: Blob): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const buffer = await blob.arrayBuffer();
    const tx = db.transaction(CHUNK_STORE, 'readwrite');
    tx.objectStore(CHUNK_STORE).add({ recordingId, buffer });
    await txDone(tx);
  } catch {
    /* degrada para memoria */
  }
}

/** Marca a gravacao como finalizada (stop normal): deixa de ser "recuperavel". */
export async function markFinished(recordingId: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const tx = db.transaction(META_STORE, 'readwrite');
    const store = tx.objectStore(META_STORE);
    const meta = (await reqToPromise(store.get(recordingId))) as RecordingMeta | undefined;
    if (meta) {
      meta.finished = true;
      store.put(meta);
    }
    await txDone(tx);
  } catch {
    /* noop */
  }
}

/** Remove TODOS os chunks + o meta de uma gravacao (apos transcrever/descartar). */
export async function deleteRecording(recordingId: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const tx = db.transaction([CHUNK_STORE, META_STORE], 'readwrite');
    const index = tx.objectStore(CHUNK_STORE).index('byRecording');
    const keys = (await reqToPromise(
      index.getAllKeys(IDBKeyRange.only(recordingId)),
    )) as IDBValidKey[];
    const chunkStore = tx.objectStore(CHUNK_STORE);
    for (const k of keys) chunkStore.delete(k);
    tx.objectStore(META_STORE).delete(recordingId);
    await txDone(tx);
  } catch {
    /* noop */
  }
}

/**
 * FASE 133 (133.C.2) — GC de gravacoes CONCLUIDAS (finished=true) mais antigas que `beforeMs` (por
 * `startedAt`). Remove chunks + meta de cada uma. Retorna quantas removeu.
 *
 * PRESERVA por design as gravacoes recuperaveis (finished=false — reload/failed, FASE 123.C): elas nunca
 * entram no filtro, INDEPENDENTE da idade. So limpa o que ja foi FINALIZADO — o fluxo de sucesso
 * (`handleRecorded`) ja apaga na hora; esta e a rede de seguranca para o que sobrar (ex.: `markFinished`
 * futuro) e para nao deixar o IndexedDB crescer sem teto. No-op silencioso sem IndexedDB (degrada como o resto).
 */
export async function pruneFinishedOlderThan(beforeMs: number): Promise<number> {
  const db = await openDb();
  if (!db) return 0;
  try {
    const metaTx = db.transaction(META_STORE, 'readonly');
    const all = (await reqToPromise(metaTx.objectStore(META_STORE).getAll())) as RecordingMeta[];
    const stale = all.filter((m) => m.finished === true && m.startedAt < beforeMs);
    for (const m of stale) {
      // eslint-disable-next-line no-await-in-loop -- IndexedDB transaction por gravacao (raro; N pequeno).
      await deleteRecording(m.recordingId);
    }
    return stale.length;
  } catch {
    return 0;
  }
}

/**
 * FASE 133 (133.C.2) — atalho: GC das gravacoes CONCLUIDAS mais velhas que `AUDIO_RECORDING_GC_MAX_AGE_MS`.
 * Chamado (fire-and-forget, best-effort) no mount do `useAudioRecorder` — o "boot" do lado web. `nowMs`
 * injetavel para teste.
 */
export async function gcFinishedRecordings(nowMs: number = Date.now()): Promise<number> {
  return pruneFinishedOlderThan(nowMs - AUDIO_RECORDING_GC_MAX_AGE_MS);
}

/**
 * Procura a gravacao INACABADA mais recente para uma chave (terminal/chat). Retorna null se nao
 * houver — usado no mount para oferecer "recuperar audio".
 */
export async function findUnfinished(key: string): Promise<RecordingMeta | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    const tx = db.transaction(META_STORE, 'readonly');
    const all = (await reqToPromise(tx.objectStore(META_STORE).getAll())) as RecordingMeta[];
    const candidates = all
      .filter((m) => m.key === key && !m.finished)
      .sort((a, b) => b.startedAt - a.startedAt);
    return candidates[0] ?? null;
  } catch {
    return null;
  }
}

/**
 * Migra gravações recuperáveis de uma chave legada para uma superfície explícita. A operação é
 * idempotente e não toca gravações finalizadas.
 */
export async function migrateUnfinishedRecordingKey(
  legacyKey: string,
  targetKey: string,
): Promise<number> {
  if (legacyKey === targetKey) return 0;
  const db = await openDb();
  if (!db) return 0;
  try {
    const tx = db.transaction(META_STORE, 'readwrite');
    const store = tx.objectStore(META_STORE);
    const all = (await reqToPromise(store.getAll())) as RecordingMeta[];
    const candidates = all.filter((meta) => meta.key === legacyKey && !meta.finished);
    for (const meta of candidates) store.put({ ...meta, key: targetKey });
    await txDone(tx);
    return candidates.length;
  } catch {
    return 0;
  }
}

/** Lista TODAS as gravacoes inacabadas (qualquer chave) — para limpeza/diagnostico. */
export async function listUnfinished(): Promise<RecordingMeta[]> {
  const db = await openDb();
  if (!db) return [];
  try {
    const tx = db.transaction(META_STORE, 'readonly');
    const all = (await reqToPromise(tx.objectStore(META_STORE).getAll())) as RecordingMeta[];
    return all.filter((m) => !m.finished).sort((a, b) => b.startedAt - a.startedAt);
  } catch {
    return [];
  }
}

/**
 * Monta o Blob completo a partir dos chunks persistidos de uma gravacao (em ordem de insercao).
 * Retorna null se nao houver chunks. mimeType vem do meta.
 */
export async function assembleBlob(recordingId: string): Promise<Blob | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    const metaTx = db.transaction(META_STORE, 'readonly');
    const meta = (await reqToPromise(metaTx.objectStore(META_STORE).get(recordingId))) as
      | RecordingMeta
      | undefined;
    const tx = db.transaction(CHUNK_STORE, 'readonly');
    const index = tx.objectStore(CHUNK_STORE).index('byRecording');
    const rows = (await reqToPromise(index.getAll(IDBKeyRange.only(recordingId)))) as Array<{
      id: number;
      recordingId: string;
      buffer: ArrayBuffer;
    }>;
    if (rows.length === 0) return null;
    // getAll pelo index ja vem na ordem das chaves primarias (autoincrement = ordem de insercao).
    const parts = rows.map((r) => r.buffer);
    return new Blob(parts, { type: meta?.mimeType || 'audio/webm' });
  } catch {
    return null;
  }
}

/** Aguarda o commit de uma transacao de escrita. */
function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/** TEST-ONLY: reseta o singleton da conexao (entre testes que recriam o fake IndexedDB). */
export function __resetDbForTests(): void {
  dbPromise = null;
}
